<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import WootSnackbar from './Snackbar.vue';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  duration: {
    type: Number,
    default: 2500,
  },
});

const { t } = useI18n();

const snackMessages = ref([]);
const snackMessagesEnd = ref([]);
const snackbarContainer = ref(null);

const showPopover = el => {
  try {
    if (el?.matches(':popover-open')) {
      el.hidePopover();
    }
    el?.showPopover();
  } catch (e) {
    // ignore
  }
};

const showPopoverCenter = () => showPopover(snackbarContainer.value);

const onNewToastMessage = ({
  message: originalMessage,
  title: originalTitle,
  action,
}) => {
  /* Toast payloads may pass raw strings or i18n keys from callers. */
  /* eslint-disable @intlify/vue-i18n/no-dynamic-keys */
  const duration = action?.duration || props.duration;
  const isEnd =
    action?.placement === 'bottom-end' || action?.placement === 'top-end';
  const targetList = isEnd ? snackMessagesEnd : snackMessages;
  const key = Date.now();

  let title = '';
  const ti = action?.titleI18n;
  if (ti?.key) {
    const values = { ...(ti.values || {}) };
    if (
      ti.fallbackKeys?.sender &&
      (values.sender == null || String(values.sender).trim() === '')
    ) {
      values.sender = t(ti.fallbackKeys.sender);
    }
    if (
      ti.fallbackKeys?.room &&
      (values.room == null || String(values.room).trim() === '')
    ) {
      values.room = t(ti.fallbackKeys.room);
    }
    title = t(ti.key, values);
  } else {
    title =
      action?.titleUsei18n && originalTitle
        ? t(originalTitle)
        : originalTitle || '';
  }

  let message = '';
  if (action?.usei18n) {
    message = t(originalMessage);
  } else {
    message = originalMessage;
  }
  if (action?.messageFallbackKey && (!message || !String(message).trim())) {
    message = t(action.messageFallbackKey);
  }

  const resolvedAction = action ? { ...action } : {};
  if (resolvedAction.usei18nLink && resolvedAction.linkLabelKey) {
    resolvedAction.linkLabel = t(resolvedAction.linkLabelKey);
  }

  targetList.value.push({
    key,
    title,
    message,
    action: resolvedAction,
  });

  nextTick(() => {
    if (!isEnd) showPopoverCenter();
  });

  setTimeout(() => {
    if (isEnd) {
      const idx = snackMessagesEnd.value.findIndex(s => s.key === key);
      if (idx >= 0) snackMessagesEnd.value.splice(idx, 1);
    } else {
      const idx = snackMessages.value.findIndex(s => s.key === key);
      if (idx >= 0) snackMessages.value.splice(idx, 1);
    }
  }, duration);
  /* eslint-enable @intlify/vue-i18n/no-dynamic-keys */
};

onMounted(() => {
  emitter.on('newToastMessage', onNewToastMessage);
});

onUnmounted(() => {
  emitter.off('newToastMessage', onNewToastMessage);
});
</script>

<template>
  <div
    ref="snackbarContainer"
    popover="manual"
    class="fixed top-4 left-1/2 z-[100000] max-w-[25rem] w-[calc(100%-2rem)] -translate-x-1/2 border-0 bg-transparent p-0 text-center outline-none m-0 overflow-visible"
  >
    <transition-group name="toast-fade" tag="div">
      <WootSnackbar
        v-for="snackMessage in snackMessages"
        :key="snackMessage.key"
        :title="snackMessage.title"
        :message="snackMessage.message"
        :action="snackMessage.action"
      />
    </transition-group>
  </div>
  <div
    class="fixed bottom-4 right-4 z-[100000] flex max-w-[min(100%-2rem,28rem)] flex-col items-end border-0 bg-transparent p-0 m-0 overflow-visible"
  >
    <transition-group
      name="toast-fade"
      tag="div"
      class="flex flex-col items-end"
    >
      <WootSnackbar
        v-for="snackMessage in snackMessagesEnd"
        :key="snackMessage.key"
        :title="snackMessage.title"
        :message="snackMessage.message"
        :action="snackMessage.action"
      />
    </transition-group>
  </div>
</template>
